import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import PageContainer from '../components/PageContainer';
import SimilarItemSuggestionPreview from '../components/SimilarItemSuggestionPreview';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import { visuallyHiddenSx } from '../utils/visuallyHidden';
import {
  SimilarItemApplyResponse,
  SimilarItemPreviewResponse,
  SimilarItemSuggestionListItem,
  applySimilarItemBatch,
  approveSimilarItemBatch,
  deferSimilarItemBatch,
  fetchSimilarItemPreview,
  fetchSimilarItemSuggestions,
  getSimilarItemErrorClass,
  parseSimilarItemApiError,
  rejectSimilarItemBatch,
  undoSimilarItemBatch,
} from '../api/classificationSuggestions';

const SimilarItemSuggestionsPage: React.FC = () => {
  const { bootstrap, loading: bootstrapLoading } = useAppBootstrap();
  const similarItemsEnabled = bootstrap?.capabilities?.similar_item_suggestions_enabled ?? false;
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedBatchId = searchParams.get('batch_id');

  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<SimilarItemSuggestionListItem[]>([]);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<SimilarItemPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewNotFound, setPreviewNotFound] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [ignoring, setIgnoring] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [completionToast, setCompletionToast] = useState<{
    severity: 'success' | 'warning' | 'error';
    message: string;
    batchId?: string;
    undoExpiresAt?: string | null;
    appliedCount?: number;
  } | null>(null);
  const [undoLoading, setUndoLoading] = useState(false);

  const isMountedRef = useRef(true);
  const previewRequestIdRef = useRef(0);

  const clearSelectedBatch = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('batch_id');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleClosePreview = useCallback(() => {
    previewRequestIdRef.current += 1;
    clearSelectedBatch();
    setPreview(null);
    setPreviewError(null);
    setPreviewNotFound(false);
    setPreviewLoading(false);
    setApproveError(null);
    setApproving(false);
    setRejecting(false);
    setIgnoring(false);
    setDecisionError(null);
  }, [clearSelectedBatch]);

  const markBatchDecisionUnavailable = useCallback(() => {
    previewRequestIdRef.current += 1;
    clearSelectedBatch();
    setPreview(null);
    setPreviewNotFound(true);
    setApproveError(null);
    setDecisionError(null);
  }, [clearSelectedBatch]);

  const resolveApplyToastSeverity = useCallback((result: SimilarItemApplyResponse) => {
    if (result.failed_count > 0 || result.applied_count === 0) {
      return 'warning' as const;
    }
    return 'success' as const;
  }, []);

  const buildApplyToastMessage = useCallback((result: SimilarItemApplyResponse) => {
    const parts = [
      `Updated domain on ${result.applied_count} item${result.applied_count === 1 ? '' : 's'}.`,
    ];
    if (result.skipped_count > 0) {
      parts.push(`${result.skipped_count} skipped.`);
    }
    if (result.failed_count > 0) {
      parts.push(`${result.failed_count} failed.`);
    }
    return parts.join(' ');
  }, []);

  const selectBatch = useCallback((batchId: string) => {
    setPreviewNotFound(false);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('batch_id', batchId);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const refreshSuggestions = useCallback(async () => {
    try {
      const response = await fetchSimilarItemSuggestions();
      if (!isMountedRef.current) return;
      setSuggestions(response.suggestions);
      setLoadError(null);
      setFeatureDisabled(false);
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const errorClass = getSimilarItemErrorClass(err);
      if (errorClass === 'similar_item_suggestions_disabled') {
        setFeatureDisabled(true);
        setSuggestions([]);
        setLoadError(null);
        return;
      }
      setLoadError(parseSimilarItemApiError(err, 'Failed to load similar-item suggestions.'));
    }
  }, []);

  const handleDecisionError = useCallback(async (
    err: unknown,
    fallbackMessage: string,
  ) => {
    const errorClass = getSimilarItemErrorClass(err);
    if (errorClass === 'similar_item_suggestions_disabled') {
      setFeatureDisabled(true);
      return;
    }
    const axiosErr = err as { response?: { status?: number } };
    const isUnavailable =
      axiosErr.response?.status === 404
      || errorClass === 'invalid_batch_status'
      || errorClass === 'similar_item_not_found';
    if (isUnavailable) {
      await refreshSuggestions();
      markBatchDecisionUnavailable();
      setStatusMessage(
        parseSimilarItemApiError(err, 'This suggestion is no longer available.'),
      );
      return;
    }
    const message = parseSimilarItemApiError(err, fallbackMessage);
    setDecisionError(message);
    setStatusMessage(message);
  }, [markBatchDecisionUnavailable, refreshSuggestions]);

  const handleRejectBatch = useCallback(async () => {
    if (!selectedBatchId) return;
    setRejecting(true);
    setApproveError(null);
    setDecisionError(null);
    try {
      setStatusMessage('Rejecting suggestion…');
      await rejectSimilarItemBatch(selectedBatchId);
      if (!isMountedRef.current) return;
      await refreshSuggestions();
      handleClosePreview();
      setStatusMessage('Suggestion rejected.');
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      await handleDecisionError(err, 'Failed to reject suggestion.');
    } finally {
      if (isMountedRef.current) {
        setRejecting(false);
      }
    }
  }, [handleClosePreview, handleDecisionError, refreshSuggestions, selectedBatchId]);

  const handleIgnoreBatch = useCallback(async () => {
    if (!selectedBatchId) return;
    setIgnoring(true);
    setApproveError(null);
    setDecisionError(null);
    try {
      setStatusMessage('Dismissing suggestion…');
      await deferSimilarItemBatch(selectedBatchId);
      if (!isMountedRef.current) return;
      await refreshSuggestions();
      handleClosePreview();
      setStatusMessage('Suggestion dismissed.');
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      await handleDecisionError(err, 'Failed to dismiss suggestion.');
    } finally {
      if (isMountedRef.current) {
        setIgnoring(false);
      }
    }
  }, [handleClosePreview, handleDecisionError, refreshSuggestions, selectedBatchId]);

  const handleApproveBatch = useCallback(async () => {
    if (!selectedBatchId || !preview) return;
    const startingStatus = preview.status;
    const needsApprove = startingStatus === 'pending_review';
    const canApply = startingStatus === 'pending_review'
      || startingStatus === 'approved'
      || startingStatus === 'applying';
    if (!canApply) return;

    setApproving(true);
    setApproveError(null);
    setDecisionError(null);
    let phase: 'approve' | 'apply' = needsApprove ? 'approve' : 'apply';
    try {
      if (needsApprove) {
        setStatusMessage('Approving suggestion…');
        await approveSimilarItemBatch(selectedBatchId);
        if (!isMountedRef.current) return;
        setPreview((current) => (current ? { ...current, status: 'approved' } : current));
      }

      phase = 'apply';
      setStatusMessage(
        startingStatus === 'applying'
          ? 'Resuming apply…'
          : 'Applying domain changes…',
      );
      const applyResult = await applySimilarItemBatch(selectedBatchId);
      if (!isMountedRef.current) return;

      if (applyResult.status === 'failed') {
        await refreshSuggestions();
        setPreview((current) => (current ? { ...current, status: 'failed' } : current));
        const message = buildApplyToastMessage(applyResult);
        setApproveError(message);
        setStatusMessage(message);
        return;
      }

      if (applyResult.status === 'completed') {
        await refreshSuggestions();
        handleClosePreview();
        setCompletionToast({
          severity: resolveApplyToastSeverity(applyResult),
          message: buildApplyToastMessage(applyResult),
          batchId: applyResult.batch_id,
          undoExpiresAt: applyResult.undo_expires_at,
          appliedCount: applyResult.applied_count,
        });
        setStatusMessage(
          `Apply complete. ${applyResult.applied_count} applied, ${applyResult.skipped_count} skipped.`,
        );
      }
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const errorClass = getSimilarItemErrorClass(err);
      if (errorClass === 'similar_item_suggestions_disabled') {
        setFeatureDisabled(true);
        return;
      }
      if (phase === 'apply') {
        await refreshSuggestions();
        if (selectedBatchId) {
          try {
            const refreshed = await fetchSimilarItemPreview(selectedBatchId);
            if (isMountedRef.current) {
              setPreview(refreshed);
            }
          } catch {
            // Preview may 404 for terminal states; keep existing preview for retry UX.
          }
        }
      }
      const fallback = phase === 'apply'
        ? 'Failed to apply domain changes.'
        : 'Failed to approve suggestion.';
      const message = parseSimilarItemApiError(err, fallback);
      setApproveError(message);
      setStatusMessage(message);
    } finally {
      if (isMountedRef.current) {
        setApproving(false);
      }
    }
  }, [
    buildApplyToastMessage,
    handleClosePreview,
    preview,
    refreshSuggestions,
    resolveApplyToastSeverity,
    selectedBatchId,
  ]);

  const handleUndoBatch = useCallback(async () => {
    if (!completionToast?.batchId) return;
    setUndoLoading(true);
    setStatusMessage('Undoing domain changes…');
    try {
      const result = await undoSimilarItemBatch(completionToast.batchId);
      if (!isMountedRef.current) return;
      setCompletionToast({
        severity: 'success',
        message: `Restored ${result.restored_count} item${result.restored_count === 1 ? '' : 's'}.`,
      });
      setStatusMessage(`Undo complete. ${result.restored_count} restored.`);
      await refreshSuggestions();
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      setCompletionToast({
        severity: 'error',
        message: parseSimilarItemApiError(err, 'Failed to undo domain changes.'),
      });
      setStatusMessage(parseSimilarItemApiError(err, 'Failed to undo domain changes.'));
    } finally {
      if (isMountedRef.current) {
        setUndoLoading(false);
      }
    }
  }, [completionToast?.batchId, refreshSuggestions]);

  const loadPreview = useCallback(async (batchId: string) => {
    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;
    setPreviewLoading(true);
    setPreview(null);
    setPreviewError(null);
    setPreviewNotFound(false);
    try {
      const response = await fetchSimilarItemPreview(batchId);
      if (!isMountedRef.current || previewRequestIdRef.current !== requestId) return;
      setPreview(response);
    } catch (err: unknown) {
      if (!isMountedRef.current || previewRequestIdRef.current !== requestId) return;
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        setPreviewNotFound(true);
        clearSelectedBatch();
        return;
      }
      const errorClass = getSimilarItemErrorClass(err);
      if (errorClass === 'similar_item_suggestions_disabled') {
        setFeatureDisabled(true);
        clearSelectedBatch();
        return;
      }
      setPreviewError(parseSimilarItemApiError(err, 'Failed to load suggestion preview.'));
    } finally {
      if (isMountedRef.current && previewRequestIdRef.current === requestId) {
        setPreviewLoading(false);
      }
    }
  }, [clearSelectedBatch]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (bootstrapLoading) return;
    if (!similarItemsEnabled) {
      setFeatureDisabled(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    void refreshSuggestions().finally(() => {
      if (isMountedRef.current) {
        setLoading(false);
      }
    });
  }, [bootstrapLoading, refreshSuggestions, similarItemsEnabled]);

  useEffect(() => {
    if (!selectedBatchId || featureDisabled) {
      previewRequestIdRef.current += 1;
      setPreview(null);
      setPreviewError(null);
      setPreviewNotFound(false);
      setPreviewLoading(false);
      return;
    }
    void loadPreview(selectedBatchId);
  }, [featureDisabled, loadPreview, selectedBatchId]);

  useEffect(() => {
    if (!selectedBatchId || preview?.status !== 'applying') {
      return undefined;
    }
    const interval = setInterval(() => {
      void loadPreview(selectedBatchId);
    }, 2000);
    return () => clearInterval(interval);
  }, [loadPreview, preview?.status, selectedBatchId]);

  const showPreviewPanel = Boolean(selectedBatchId);
  const showEmptyState = !loading && !featureDisabled && suggestions.length === 0 && !showPreviewPanel;
  const showSuggestionSection = !loading && !featureDisabled && (suggestions.length > 0 || showPreviewPanel);

  return (
    <PageContainer>
      <Box className="space-y-4">
        <Box>
          <Typography variant="h4" component="h1" className="font-semibold">
            Similar item suggestions
          </Typography>
          <Typography variant="body1" color="text.secondary" className="mt-1">
            Review items that may belong with a domain you corrected. Approve before any bulk domain change.
          </Typography>
        </Box>

        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="SIMILAR_ITEM_STATUS"
          sx={visuallyHiddenSx}
        >
          {statusMessage}
        </Box>

        {loading ? (
          <Box className="flex justify-center py-8" data-testid="SIMILAR_ITEM_LOADING">
            <CircularProgress aria-label="Loading similar-item suggestions" />
          </Box>
        ) : null}

        {featureDisabled ? (
          <Alert severity="info" data-testid="SIMILAR_ITEM_DISABLED">
            Similar-item suggestions are not available in this environment yet.
          </Alert>
        ) : null}

        {loadError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refreshSuggestions()}>
                Retry
              </Button>
            }
            data-testid="SIMILAR_ITEM_LOAD_ERROR"
          >
            {loadError}
          </Alert>
        ) : null}

        {showEmptyState ? (
          <Alert severity="info" data-testid="SIMILAR_ITEM_EMPTY">
            No similar-item suggestions right now. Correct a WorkItem domain to discover related items.
          </Alert>
        ) : null}

        {showSuggestionSection ? (
          <Box
            className={
              suggestions.length > 0 && showPreviewPanel
                ? 'grid gap-4 md:grid-cols-2 md:items-start'
                : 'grid gap-4'
            }
          >
            {suggestions.length > 0 ? (
              <Box>
                <Typography variant="h6" component="h2" className="mb-3">
                  Pending suggestions ({suggestions.length})
                </Typography>
                <List data-testid="SIMILAR_ITEM_LIST">
                  {suggestions.map((suggestion) => {
                    const isSelected = selectedBatchId === suggestion.batch_id;
                    return (
                      <ListItemButton
                        key={suggestion.batch_id}
                        divider
                        selected={isSelected}
                        aria-selected={isSelected}
                        tabIndex={0}
                        data-batch-id={suggestion.batch_id}
                        data-testid={`SIMILAR_ITEM_ROW_${suggestion.batch_id}`}
                        onClick={() => selectBatch(suggestion.batch_id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            selectBatch(suggestion.batch_id);
                          }
                        }}
                      >
                        <ListItemText
                          primary={`${suggestion.prior_domain} → ${suggestion.target_domain}`}
                          secondary={`${suggestion.affected_count} items · ${suggestion.confidence_cue}${suggestion.similarity_summary ? ` · ${suggestion.similarity_summary}` : ''}`}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            ) : null}

            {showPreviewPanel ? (
              <Box data-testid="SIMILAR_ITEM_PREVIEW_PANEL">
                <SimilarItemSuggestionPreview
                  loading={previewLoading}
                  preview={preview}
                  error={previewError}
                  notFound={previewNotFound}
                  approving={approving}
                  approveError={approveError}
                  rejecting={rejecting}
                  ignoring={ignoring}
                  decisionError={decisionError}
                  onClose={handleClosePreview}
                  onRetry={() => {
                    if (selectedBatchId) {
                      void loadPreview(selectedBatchId);
                    }
                  }}
                  onApprove={() => {
                    void handleApproveBatch();
                  }}
                  onReject={() => {
                    void handleRejectBatch();
                  }}
                  onIgnore={() => {
                    void handleIgnoreBatch();
                  }}
                />
              </Box>
            ) : null}
          </Box>
        ) : null}

        <Snackbar
          open={Boolean(completionToast)}
          autoHideDuration={8000}
          onClose={() => setCompletionToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setCompletionToast(null)}
            severity={completionToast?.severity ?? 'info'}
            variant="filled"
            data-testid="SIMILAR_ITEM_COMPLETION_TOAST"
            action={
              completionToast?.batchId
              && completionToast.undoExpiresAt
              && (completionToast.appliedCount ?? 0) > 0 ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={undoLoading}
                  onClick={() => void handleUndoBatch()}
                  data-testid="SIMILAR_ITEM_UNDO_BUTTON"
                >
                  Undo
                </Button>
              ) : undefined
            }
          >
            {completionToast?.message}
          </Alert>
        </Snackbar>
      </Box>
    </PageContainer>
  );
};

export default SimilarItemSuggestionsPage;
